<script lang="ts">
  import { replace } from "svelte-spa-router";
  import SpotClassPreview from "./SpotClassPreview.svelte";
  import { Encode } from "$shared/lib/query";
  import api, { fromApi } from "$shared/lib/api-parkito2.svelte.ts";
  import { derived } from "svelte/store";
  import type { Debtor } from "$apis/parkito2/index.ts";
  import { DateModel } from "$models";
  import { formatDate } from "$shared/utils/dateTime.ts";
  import {
    Button,
    ComfortCol,
    Header,
    Label,
    Page,
    Separator,
    Spacer,
    Toast,
    ValCheckbox,
    ValInputField,
    Ver,
    WithResource,
  } from "$components";
  import { myPages } from "../../stores/myPagesData.svelte.ts";
  import { writable } from "svelte/store";
  import { onMount } from "svelte";
  import SimplifiedDebtorSelector from "./SimplifiedDebtorSelector.svelte";
  import type {
    NewQueueApplicant,
    QueueApplicantVolition,
  } from "$apis/parkito";
  import { Decode } from "$shared/lib/query";
  import { Form, Validate } from "$shared/lib/validation_next";

  const { params }: { params: { site_id: string; queue_id: string } } =
    $props();

  const query = Decode.query({
    tags: Decode.array<string>,
    startDate: Decode.date("YYYY-MM-DD"),
  }).optional();

  const queueReq = fromApi(() =>
    api.queueGet({ path: { id: params.queue_id } })
  );
  onMount(() => $queueReq.load());

  const queueApplicationReq = fromApi((applicant: NewQueueApplicant) =>
    api.queuePostApplicant({
      body: applicant,
      path: { id: params.queue_id },
    })
  );

  const today = formatDate(new Date(), "YYYY-MM-DD");
  // -- Start date
  let fieldStartDate = Form.field(
    Validate.required("Startdatum måste anges"),
    query?.startDate.format("YYYY-MM-DD") ?? today,
  );

  const fieldResidentalParking = Form.checkbox(Validate.boolean());

  const selectedDebtor = writable<Debtor | undefined>(undefined);

  const selectedDebtorId = derived(selectedDebtor, (sb) => sb?.id);
  const volition = Form.object<QueueApplicantVolition>({
    first: Form.nest(Form.object({
      tags: Form.fixed(query?.tags.map((tagId) => ({ tagId })) ?? []),
    })),
  });

  const queueForm = Form.object<NewQueueApplicant>({
    debtorId: Form.fromStore(selectedDebtorId),
    preferredStartDate: fieldStartDate,
    volition: Form.nest(volition),
    wantsResidentalVatFree: fieldResidentalParking,
  });

  async function handleSubmit() {
    if ($queueForm.res.isErr()) {
      Toast.error("Fel i formulär");
      return;
    }

    const applicant = $queueForm.res.value;
    const res = await $queueApplicationReq.load(applicant);
    if (res.error) {
      Toast.error("Något gick fel");
    } else {
      const q = Encode.asQuery({
        type: "queue",
        applicantId: applicant.debtorId,
      });
      replace(`/store/${params.site_id}/permit/checkout/success?${q}`);
    }
  }
</script>

<Page backButton title={$queueReq.call?.data?.name ?? "Ny intresseanmälan"}>
  {#snippet content()}
    <ComfortCol>
      <Ver gap="m">
        <WithResource api={queueReq}>
          {#snippet error(err)}
            Kunde inte ladda kölistan: {err}
          {/snippet}
          {#snippet child(_queue)}
            <Header size="4">Önskat startdatum *</Header>
            <ValInputField
              name="startdate"
              label="Önskat startdatum för att börja använda tjänsten, fakturering sker enligt avtalets startdatum"
              field={fieldStartDate}
              type="date"
            />
            <Spacer />

            <WithResource api={myPages}>
              {#snippet child(myPagesData)}
                <Header size="3">Din profil</Header>
                <SimplifiedDebtorSelector
                  reloadDebtors={$myPages.reload}
                  myPages={myPagesData}
                  {selectedDebtor}
                />
              {/snippet}
            </WithResource>

            <Spacer />
            <Header size="4">Plats</Header>
            {#if query}
              <SpotClassPreview
                siteId={params.site_id}
                startDate={new DateModel($fieldStartDate.res.unwrapOr(today))}
                spotClassId={query?.tags}
              />
            {:else}
              <Label type="error"
              >Något har gått snett. Länken du har fått kan vara trasig</Label>
            {/if}

            <Spacer />

            <Header size="4">Boende</Header>
            <ValCheckbox
              name="residental-parking"
              field={fieldResidentalParking}
              label="Boende i fastigheten"
            />
            <Separator customClass="max-mobile:hidden my-[24px]" />
            <Button
              onClick={handleSubmit}
              text="Ställ dig i kölistan"
            ></Button>
          {/snippet}
        </WithResource>
      </Ver>
    </ComfortCol>
  {/snippet}
</Page>
